import { api } from './api';

interface CacheEntry {
  url: string;
  timestamp: number;
}

// Avatar cache with expiry tracking
const avatarCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 55 * 60 * 1000; // 55 minutes (refresh before 1-hour expiry)
const requestMap = new Map<string, Promise<string | null>>(); // Deduplicate requests

function isCacheValid(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp < CACHE_DURATION;
}

export async function getAvatarUrl(userId: string): Promise<string | null> {
  try {
    // Don't use cache - always fetch fresh to ensure correct URL format
    // const cached = avatarCache.get(userId);
    // if (cached && isCacheValid(cached)) {
    //   console.log('[avatarUtils] Returning cached avatar URL for user:', userId);
    //   return cached.url;
    // }

    // If already fetching this user, return the pending request
    if (requestMap.has(userId)) {
      console.log('[avatarUtils] Using pending request for user:', userId);
      return requestMap.get(userId)!;
    }

    // Create the request and store it
    const request = (async () => {
      try {
        console.log('[avatarUtils] Fetching fresh avatar URL for user:', userId);
        const response = await api.get<{ url: string }>(`/admin/users/avatar/${userId}`);

        // Cache the URL with timestamp
        avatarCache.set(userId, {
          url: response.url,
          timestamp: Date.now()
        });
        console.log('[avatarUtils] Cached avatar URL for user:', userId);

        return response.url;
      } catch (error) {
        console.error('[avatarUtils] Failed to get avatar URL:', error);
        return null;
      } finally {
        // Remove from pending requests
        requestMap.delete(userId);
      }
    })();

    requestMap.set(userId, request);
    return request;
  } catch (error) {
    console.error('[avatarUtils] Failed to get avatar URL:', error);
    return null;
  }
}

/**
 * Batch fetch multiple avatar URLs in parallel
 * Much more efficient than calling getAvatarUrl multiple times
 */
export async function batchGetAvatarUrls(userIds: string[]): Promise<Map<string, string | null>> {
  const results = new Map<string, string | null>();
  const promises = userIds.map(async (userId) => {
    const url = await getAvatarUrl(userId);
    results.set(userId, url);
  });

  await Promise.all(promises);
  return results;
}

export function clearAvatarCache(userId?: string): void {
  if (userId) {
    console.log('[avatarUtils] Clearing cache for user:', userId);
    avatarCache.delete(userId);
  } else {
    console.log('[avatarUtils] Clearing entire avatar cache');
    avatarCache.clear();
  }
}

export function getCachedAvatarUrl(userId: string): string | null | undefined {
  const cached = avatarCache.get(userId);
  if (cached && isCacheValid(cached)) {
    return cached.url;
  }
  // Remove expired cache
  if (cached) {
    avatarCache.delete(userId);
  }
  return undefined;
}
